import { pathOr } from 'ramda';
import moment from 'moment';
import { parseAffordableRent } from '../../utils/lease-helpers';

const HUD_FLOORPLAN = 'HUD';
const RD_FLOORPLAN = 'RD';
const LIHTC_FLOORPLAN = 'LIHTC';
const BOND_FLOORPLAN = 'BOND';

export const getAffordablePlanDetails = (floorplans: Array<Object>) => {
  return floorplans.reduce(
    (acc, floorplan) => {
      const { isHUD, isLIHTC, isRD } = getFloorPlanType(floorplan);
      if (acc.hasHUD === true && acc.hasLIHTC === true) return acc;
      if (isHUD === true) {
        acc.hasHUD = true;
      }
      if (isLIHTC === true) {
        acc.hasLIHTC = true;
      }
      if (isRD === true) {
        acc.hasRD = true;
      }
      return acc;
    },
    {
      hasHUD: false,
      hasLIHTC: false,
      hasRD: false,
    },
  );
};

export const getFloorPlanType = (floorPlan: Object) => {
  const fpAffordablePrograms = pathOr(
    [],
    ['floorPlanAffordablePrograms'],
    floorPlan,
  );
  const fpType = fpAffordablePrograms.reduce(
    (acc, program) => {
      const name = pathOr(
        '',
        ['propertyAffordableProgram', 'masterAffordableProgram', 'name'],
        program,
      );
      switch (name) {
        case HUD_FLOORPLAN: {
          acc.isHUD = true;
          break;
        }

        case RD_FLOORPLAN: {
          acc.isRD = true;
          break;
        }
        case LIHTC_FLOORPLAN:
        case BOND_FLOORPLAN: {
          acc.isLIHTC = true;
          break;
        }
        default:
          return acc;
      }
      return acc;
    },
    {
      isHUD: false,
      isLIHTC: false,
      isRD: false,
    },
  );
  return { ...fpType };
};

// Shown in the Programs column when a floor plan carries no affordable programs — i.e. it is
// conventional. Passed explicitly rather than relying on the component library's DASHES fallback,
// which only applies to nullish values; an empty array would render as a blank cell.
export const NO_PROGRAMS_PLACEHOLDER = '---';

/**
 * Master affordable program names carried by a floor plan, for the Manage Floor Plans Programs
 * column. Sorted alphabetically; duplicates are collapsed.
 *
 * Layered program names are kept WHOLE on purpose: 'LIHTC/HOME' is a single program the property
 * has configured, so it must render as one entry. Splitting on '/' — as some backend consumers do
 * when they need the constituent programs — would wrongly show it as two.
 */
export const getFloorPlanProgramNames = (floorPlan: Object): Array<string> => {
  const fpAffordablePrograms = floorPlan?.floorPlanAffordablePrograms ?? [];

  const names = new Set(
    fpAffordablePrograms
      .map(
        (program) =>
          program?.propertyAffordableProgram?.masterAffordableProgram?.name ??
          '',
      )
      .filter(Boolean),
  );

  return (
    [...names]
      // localeCompare rather than the default codepoint sort, so mixed-case or accented
      // program names order the way a reader expects.
      .sort((left, right) => left.localeCompare(right))
  );
};

/**
 * Reconciles the persisted column-visibility state with the current header set.
 *
 * `useTableManageColumns` saves the user's visible columns under
 * `<name>_manageColumns_showHide_state`. A column added to HEADERS afterwards is absent from that
 * saved list, so for anyone who has already opened the screen it is treated as hidden and never
 * renders — which is exactly why the Programs column stayed invisible.
 *
 * When the saved list already covers every current header we leave the user's choices alone. When a
 * header is missing we force a one-time overwrite so the defaults (all columns visible) apply.
 *
 * Caveat: a column the user deliberately hid is indistinguishable from one that did not exist yet,
 * so that one-time reset also restores any columns they had hidden. Without a stored schema version
 * there is no way to tell the two apart, and this matches the existing convention elsewhere
 * (RentForecast/hooks.js, Portfolio/hooksV2.js).
 */
export const getStoredColumnVisibilityState = ({
  headers = [],
  name = '',
}: {
  headers: Array<Object>,
  name: string,
}): {
  initialSelectedColumns: Array<string> | void,
  shouldForceOverwrite: boolean,
} => {
  const allColumnIds = headers.map(({ id }) => id);
  // Verified against the library hook: `forceOverwrite` alone does NOT clear the saved visibility
  // state, and `initialSelectedColumns` alone is ignored while a saved state exists. Only the two
  // together restore the column, so they are always returned as a pair.
  const migrate = {
    initialSelectedColumns: allColumnIds,
    shouldForceOverwrite: true,
  };
  const keepUserChoices = {
    initialSelectedColumns: undefined,
    shouldForceOverwrite: false,
  };

  try {
    const stored = localStorage.getItem(`${name}_manageColumns_showHide_state`);
    if (!stored) {
      // Nothing saved yet: the hook's defaults already show every column.
      return keepUserChoices;
    }

    const visibleColumnIds = JSON.parse(stored);
    if (!Array.isArray(visibleColumnIds)) {
      return migrate;
    }

    const hasEveryHeader = allColumnIds.every((id) =>
      visibleColumnIds.includes(id),
    );

    return hasEveryHeader ? keepUserChoices : migrate;
  } catch {
    // Unreadable/corrupt saved value — fall back to the defaults.
    return migrate;
  }
};

/**
 * Programs cell value for the Manage Floor Plans table: a single comma-separated line
 * ("HUD, LIHTC/HOME"), or the placeholder for a conventional floor plan.
 */
export const getFloorPlanProgramsCellValue = (
  programNames: ?Array<string>,
): string =>
  programNames?.length ? programNames.join(', ') : NO_PROGRAMS_PLACEHOLDER;

export const parse236Rents = ({ floorPlan }) => {
  const hud236BasicRent = floorPlan?.hud236BasicRent
    ? parseAffordableRent({
        affordableFloorplanPricing: floorPlan,
        date: moment(),
        rentListName: 'fp236BasicRents',
        floorPlanRentName: 'hud236BasicRent',
      })
    : 0;

  const hud236MarketRent = floorPlan?.hud236MarketRent
    ? parseAffordableRent({
        affordableFloorplanPricing: floorPlan,
        date: moment(),
        rentListName: 'fp236MarketRents',
        floorPlanRentName: 'hud236MarketRent',
      })
    : 0;

  return {
    hud236BasicRent,
    hud236MarketRent,
  };
};
